import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Download, Loader2, BookOpen, ArrowRight, Twitter, Facebook, Linkedin, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { fetchLeadMagnet, claimLeadMagnet, API, BACKEND_URL } from "@/lib/api";

/**
 * Free-ebook landing page — `/free-ebook`.
 *
 * Two states:
 *   1. Pre-submit: hero + book cover + email form
 *   2. Post-submit: "check your inbox" + direct download button + share strip
 */
export default function FreeEbook() {
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [claim, setClaim] = useState(null);
  const [searchParams] = useSearchParams();
  const source = searchParams.get("s") || "landing";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const e = await fetchLeadMagnet();
        if (!cancelled) setEbook(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    try {
      setBusy(true);
      const res = await claimLeadMagnet({
        email: form.email.trim(),
        name: form.name.trim() || null,
        source,
      });
      setClaim(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went sideways. Try again?");
    } finally {
      setBusy(false);
    }
  };

  const url = typeof window !== "undefined" ? `${window.location.origin}/free-ebook` : "/free-ebook";
  const shareText = ebook
    ? `Free ebook from Umbra Journal: ${ebook.title}${ebook.subtitle ? " — " + ebook.subtitle : ""}`
    : "Free ebook from Umbra Journal";
  const enc = encodeURIComponent;
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
  };
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Landing page URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const pageTitle = ebook
    ? `${ebook.title} — free download from Umbra Journal`
    : "A free ebook — Umbra Journal";
  const pageDesc = ebook
    ? `${ebook.subtitle || ebook.description || ""} · Free download when you leave an email.`
    : "A free ebook from Umbra Journal — leave an email, we'll deliver it.";

  return (
    <div className="min-h-screen bg-obsidian-900" data-testid="free-ebook-page">
      <Helmet>
        <html lang="en" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={url} />
        {ebook && ebook.cover_url && <meta property="og:image" content={`${BACKEND_URL}${ebook.cover_url}`} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <header className="border-b border-obsidian-600 px-6 md:px-12 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:underline"
          >
            ← Umbra
          </Link>
          <Link
            to="/blog"
            className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:underline"
          >
            The Journal
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <p className="font-sans text-bone-500 text-sm">Loading…</p>
        ) : !ebook ? (
          <div className="text-center py-16">
            <p className="font-serif italic text-bone-400 text-xl">
              The free download isn't ready yet. A new drop is being prepared — check back soon.
            </p>
            <Link
              to="/blog"
              className="inline-block mt-6 font-sans text-xs uppercase tracking-[0.24em] text-terracotta hover:underline"
            >
              Read the Journal in the meantime →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
            {/* Cover */}
            <div>
              <div className="border border-obsidian-600 bg-obsidian-800/40 aspect-[3/4] flex items-center justify-center overflow-hidden">
                {ebook.cover_url ? (
                  <img
                    src={`${BACKEND_URL}${ebook.cover_url}`}
                    alt={`Cover of ${ebook.title}`}
                    className="w-full h-full object-cover"
                    data-testid="free-ebook-cover"
                  />
                ) : (
                  <div className="text-center px-6" aria-hidden="true">
                    <BookOpen className="h-12 w-12 text-bone-600 mx-auto mb-4" />
                    <p className="font-serif text-2xl text-bone-400 italic">{ebook.title}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Copy + form */}
            <div>
              <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-3">
                Free download
              </p>
              <h1
                className="font-serif text-4xl sm:text-5xl text-bone-100 leading-tight mb-4"
                data-testid="free-ebook-title"
              >
                {ebook.title}
              </h1>
              {ebook.subtitle && (
                <p className="font-serif italic text-lg text-bone-300 mb-6">{ebook.subtitle}</p>
              )}
              {ebook.description && (
                <p className="font-serif text-[1.05rem] text-bone-300 leading-[1.75] mb-8">
                  {ebook.description}
                </p>
              )}

              {claim ? (
                <div className="border border-terracotta bg-terracotta/10 p-6" data-testid="free-ebook-success">
                  <p className="font-serif italic text-xl text-bone-100 mb-2">
                    {claim.message}
                  </p>
                  <p className="font-sans text-xs text-bone-400 mb-6">
                    Nothing in your inbox after a few minutes? Check your spam folder — or use the direct link below.
                  </p>
                  <a
                    href={claim.download_url}
                    data-testid="free-ebook-direct-download"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-[0.24em] text-xs px-6 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
                  >
                    <Download className="h-4 w-4" />
                    Download now
                  </a>

                  {/* Share strip — help the user spread the offer */}
                  <div className="mt-8 pt-6 border-t border-obsidian-600" data-testid="free-ebook-share">
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-3">
                      Pass it on
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta hover:text-terracotta text-bone-400 font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2">
                        <Twitter className="h-3 w-3" /> X
                      </a>
                      <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta hover:text-terracotta text-bone-400 font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2">
                        <Facebook className="h-3 w-3" /> Facebook
                      </a>
                      <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta hover:text-terracotta text-bone-400 font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2">
                        <Linkedin className="h-3 w-3" /> LinkedIn
                      </a>
                      <button onClick={copyShare} className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta hover:text-terracotta text-bone-400 font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2">
                        <LinkIcon className="h-3 w-3" /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="free-ebook-form">
                  <label className="block">
                    <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
                      First name (optional)
                    </span>
                    <input
                      type="text"
                      autoComplete="given-name"
                      maxLength={80}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50"
                      style={{ fontSize: "16px" }}
                    />
                  </label>
                  <label className="block">
                    <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
                      Email *
                    </span>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      inputMode="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="free-ebook-email"
                      className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50"
                      style={{ fontSize: "16px" }}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="free-ebook-submit"
                    className="w-full bg-terracotta hover:bg-terracotta-deep disabled:opacity-60 text-obsidian-900 font-sans uppercase tracking-[0.24em] text-xs py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 inline-flex items-center justify-center gap-2"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Send it to my inbox <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                  <p className="font-sans text-[0.65rem] text-bone-500 italic">
                    We'll email you the download link, then add you to the fortnightly essay list. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
